<template>
  <div class="verify-identity m-auto">
    <StepHeader
      :title="title"
      :theme="theme"
    />
    <StepContent
      :theme="theme"
      width="max-w-600"
    >
      <DocumentUpload
        :upload-buttons="uploadButtons"
        :docs-stand-alone="docsStandAlone"
        :theme="theme"
      >
        <template #step-summary>
          <p class="text-center">
            Please upload an image of the front of your Government-Issued photo ID to complete your loan application.
          </p>
        </template>
      </DocumentUpload>
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="submitting"
        @click="handleContinue"
      >
        Continue
      </AppButton>

      <StepSkip
        v-if="showSkipStep"
        verification-step="ID"
        :theme="theme"
        @skip="handleSkip"
      />
    </StepFooter>

    <div class="container">
      <div class="verify-identity__tips">
        <p>Tips on how your upload should look:</p>
        <div class="verify-identity__tips-grid">
          <div
            v-for="(tip, index) in tips"
            :key="index"
            class="verify-identity__tips-item"
          >
            <img
              :src="tip.img"
              alt=""
            >
            <span>{{ tip.caption }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepHeader from '@/components/common/step-layout/StepHeader'
import StepContent from '@/components/common/step-layout/StepContent'
import DocumentUpload from '@/components/common/document-upload/DocumentUpload'
import submitLoans from '@/mixins/submit/submit-loans'
import navigation from '@/mixins/navigation'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepSkip from '@/components/common/step-layout/StepSkip'
import AppButton from '@/components/common/buttons/AppButton'
import polling from '@/mixins/delay/polling'

export default {
  name: 'VerifyIdentity',

  // Components
  components: {
    StepHeader,
    StepContent,
    StepFooter,
    StepSkip,
    AppButton,
    DocumentUpload
  },

  // Mixins
  mixins: [
    navigation,
    submitLoans,
    polling
  ],

  // Props
  props: {
    docsStandAlone: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'green'
    }
  },

  // Computed
  computed: {

    identityFileError () {
      return this.$store.state.validations.identityFileError
    },

    showSkipStep () {
      return this.$route.meta.showSkipStep
    },

    title () {
      return 'Verify Your Identity'
    },

    tips () {
      return [
        {
          img: require('@/assets/img/documents/id/tip-1.png'),
          caption: 'Make sure all four corners are visible'
        },
        {
          img: require('@/assets/img/documents/id/tip-2.png'),
          caption: 'Cannot be covered or obstructed in any way'
        },
        {
          img: require('@/assets/img/documents/id/tip-3.png'),
          caption: 'Should not be blurry and unable to read'
        },
        {
          img: require('@/assets/img/documents/id/tip-4.png'),
          caption: 'Perfect!'
        }
      ]
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    dataLayerObject () {
      const eventName = this.returningCustomer ? `spring_${this.eventPrefix}_returning_customer_id_step_completed` : `spring_${this.eventPrefix}_id_step_completed`
      return {
        event: eventName
      }
    },

    datalayerSkip () {
      return {
        event: `spring_${this.eventPrefix}_verifyidentity_skip`
      }
    },

    dataLayerUpload () {
      const eventName = this.returningCustomer ? `spring_${this.eventPrefix}_returning_customer_id_document_upload` : `spring_${this.eventPrefix}_id_document_upload`
      return {
        event: eventName
      }
    },

    uploadButtons () {
      return [
        {
          documentType: 'Photo ID',
          title: 'Upload Your ID',
          value: this.file,
          vuexAction: 'setId',
          error: this.identityFileError,
          vuexActionFile: 'setIdFile',
          required: !this.$store.state.documents.id,
          dataLayerEvent: this.dataLayerUpload
        }
      ]
    },

    file () {
      return this.$store.state.documents.idFile
    }
  },

  // Watch
  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    },

    $_navigation_triggerSkip: function (val) {
      this.$store.dispatch('setTriggerSkipStep', false)
    }
  },

  // Methods
  methods: {
    async handleContinue () {
      const isValid = await this.$_navigation_validateStep()
      if (!isValid) return new Error('Validation failed.')
      if (this.isPersonalLoan) {
        await this.pollProductEligibility(true)
      } else {
        this.$_loans_submitLeadIfReady(false, false, this.dataLayerObject)
      }
    },

    handleSkip () {
      this.$store.dispatch('setSkippedStep', true)
      this.$_navigation_handleNext(this.datalayerSkip, true)
    }
  }
}
</script>

<style scoped lang="scss">
  .verify-identity {
    &__tips {
      @apply max-w-485 mt-5 mx-auto text-center;

      @screen sm {
        @apply mt-[-50px];
      }
    }

    &__tips-grid {
      @apply gap-x-[25px] gap-y-[30px] grid mt-15;

      grid-template-columns: 1fr 1fr;

      @screen lg {
        @apply gap-x-[55px] gap-y-[25px] mt-20;
      }
    }

    &__tips-item {
      img {
        @apply mb-5;

        @screen lg {
          @apply mb-10;
        }
      }

      span {
        @apply block px-5 text-xs;
        // % width of image excluding the x and ✓ icon
        // to make the text look more centered
        @apply w-[91%];

        @screen sm {
          @apply px-30;
        }

        @screen lg {
          @apply px-10 text-base;
        }
      }
    }
  }
</style>
